import React, { useState, useEffect, useCallback } from "react";
import {
    X, Upload, FileSpreadsheet, History, Download, Loader, CheckCircle2,
    AlertTriangle, AlertCircle, ArrowRight, Eye,
} from "lucide-react";
import api from "../../utils/api";

// Manual daily upload of the inbound (Pilot) and outbound (carrier) Excel reports.
// Flow: choose file → server preview (validate + diff) → confirm or discard.

export const EXCEPTION_LABELS = {
    STATUS_UNKNOWN:             "Unrecognised status",
    EXCEPTION_ASN:              "ASN issue",
    ASN_IN_COMMENTS:            "ASN in comments",
    DELIVERY_SITE_CORRUPTED:    "Odd delivery site",
    DESTINATION_IS_ORIGIN_PORT: "Origin port in destination",
};

export const SHEET_LABELS = {
    metro: "Metro prebooking", interstate: "Interstate prebooking",
    returns: "Return", departed: "Departed", pilot: "Inbound PO line", pallet: "Pallet program",
};

const UPLOAD_STATUS = {
    preview:    { label: "Preview",    c: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    committing: { label: "Importing",  c: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    accepted:   { label: "Current",    c: "bg-green-500/20 text-green-400 border-green-500/30" },
    superseded: { label: "Accepted",   c: "bg-slate-700 text-slate-300 border-slate-600" },
    discarded:  { label: "Discarded",  c: "bg-slate-800 text-slate-500 border-slate-700" },
    failed:     { label: "Failed",     c: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const REPORT_NAMES = {
    inbound: "Inbound — Pilot Report", outbound: "Outbound — Carrier Report", outstanding: "Outbound — Outstanding Orders (warehouse)",
    prealert: "Outbound — Mainfreight Prealert", mfexport: "Outbound — Mainfreight Pre-Alert export", tnt: "Outbound — TNT Daily Report", pallets: "Pallets — Pallet Tracking workbook",
};
// What the user can drop under each direction — the file itself is recognised from its headers
// (controlTowerRules.js on the backend), so there is no report dropdown
const DIRECTION_HINT = {
    inbound: "Hasbro Pilot Report (.xlsx)",
    outbound: "Outstanding Orders (.csv), Mainfreight Prealert (.xlsx), Mainfreight Pre-Alert export (.xlsx), TNT Daily Report (.xls), Carrier Report (.xlsx)",
};

export function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export const fieldLabel = f => f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

function fmtValue(v) {
    if (v == null || v === "") return "—";
    if (Array.isArray(v)) return v.join(", ") || "—";
    return String(v);
}

function todayLocalISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const apiError = (e, fallback) => e.response?.data?.error || fallback;

// ── Preview / result panel ───────────────────────────────────────────────────
const ITEM_FILTERS = [
    { id: "all",        label: "All changes",    test: () => true },
    { id: "new",        label: "New",            test: i => i.type === "new" },
    { id: "status",     label: "Status changed", test: i => !!i.statusChange },
    { id: "eta",        label: "ETA changed",    test: i => !!i.etaChange },
    { id: "missing",    label: "Missing",        test: i => i.type === "missing" },
    { id: "exceptions", label: "Exceptions",     test: i => i.exceptions?.length > 0 },
];

// Field changes already shown as the Status / ETA lines
const STATUS_FIELDS = ["comments", "shipment_status"];
const ETA_FIELDS = ["eta", "required_delivery_date"];
const duplicatesSummary = (field, item) =>
    (item.statusChange && STATUS_FIELDS.includes(field)) || (item.etaChange && ETA_FIELDS.includes(field));

function PreviewPanel({ data, busy, onConfirm, onDiscard, onDownload }) {
    const [filter, setFilter] = useState("all");
    const [showIssues, setShowIssues] = useState(false);
    const { upload, issues = [], items = [], itemsTotal = 0, issuesTotal = 0, statusLabels = {} } = data;
    const s = upload.summary || {};
    const errors = issues.filter(i => i.severity === "error");
    const warnings = issues.filter(i => i.severity !== "error");
    const isPreview = upload.status === "preview" || upload.status === "failed";
    const statusText = c => (c ? statusLabels[c] || c : "—");
    const shown = items.filter(ITEM_FILTERS.find(f => f.id === filter).test);

    const CARDS = [
        { label: "Rows read",       value: s.rows,           color: "#94A3B8" },
        { label: "New",             value: s.new,            color: "#10B981" },
        { label: "Changed",         value: s.changed,        color: "#3B82F6" },
        { label: "Status changes",  value: s.statusChanged,  color: "#8B5CF6" },
        { label: "ETA changes",     value: s.etaChanged,     color: "#F59E0B", sub: s.etaSlipped ? `${s.etaSlipped} later` : null },
        { label: "Missing",         value: s.missing,        color: "#F97316", sub: "not in this file" },
        { label: "With exceptions", value: s.withExceptions, color: "#EF4444" },
        { label: "Unchanged",       value: s.unchanged,      color: "#475569" },
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-bold text-white flex items-center gap-2">
                        <FileSpreadsheet size={15} className="text-green-400"/> {upload.fileName}
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${UPLOAD_STATUS[upload.status]?.c || ""}`}>
                            {UPLOAD_STATUS[upload.status]?.label || upload.status}
                        </span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                        {upload.detected?.label || upload.reportLabel || REPORT_NAMES[upload.reportType] || upload.reportType}
                        {upload.detected && <span className="text-slate-600"> (recognised from its headers)</span>} ·
                        report date {fmtDate(upload.reportDate)} · uploaded {fmtDateTime(upload.uploadedAt)}
                        {upload.uploadedBy && ` by ${upload.uploadedBy}`}
                    </p>
                    {upload.rowCounts && (
                        <p className="text-[11px] text-slate-600 mt-0.5">
                            {Object.entries(upload.rowCounts).map(([k, v]) => `${k}: ${v} rows`).join(" · ")}
                        </p>
                    )}
                </div>
                <button onClick={onDownload} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#374151] text-[11px] text-slate-300 hover:border-slate-500">
                    <Download size={12}/> Original file
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {CARDS.map(c => (
                    <div key={c.label} className="bg-[#0D1520] border border-[#1F2937] rounded-lg px-3 py-2">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{c.label}</p>
                        <p className="text-lg font-bold tabular-nums" style={{ color: c.color }}>{c.value ?? 0}</p>
                        {c.sub && <p className="text-[10px] text-slate-600">{c.sub}</p>}
                    </div>
                ))}
            </div>

            {errors.length > 0 && (
                <div className="bg-red-900/30 border border-red-800 rounded-lg px-3 py-2.5 text-[12px] text-red-300 space-y-1">
                    <p className="font-bold flex items-center gap-1.5"><AlertCircle size={13}/> This file can't be imported</p>
                    {errors.slice(0, 8).map((e, i) => (
                        <p key={i} className="text-red-400">{e.sheet ? `${e.sheet}: ` : ""}{e.message}</p>
                    ))}
                </div>
            )}

            {warnings.some(w => w.prominent) && (
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg px-3 py-2.5 text-[12px] text-amber-300 space-y-1">
                    <p className="font-bold flex items-center gap-1.5"><AlertTriangle size={13}/> Check before importing</p>
                    {warnings.filter(w => w.prominent).map((w, i) => (
                        <p key={i}>{w.sheet ? <span className="font-semibold">{w.sheet}: </span> : ""}{w.message}</p>
                    ))}
                </div>
            )}

            {warnings.length > 0 && (
                <div className="bg-[#0D1520] border border-[#1F2937] rounded-lg">
                    <button onClick={() => setShowIssues(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-[12px] text-amber-400">
                        <span className="flex items-center gap-1.5"><AlertTriangle size={13}/> {issuesTotal - errors.length} data quality warning{issuesTotal - errors.length !== 1 ? "s" : ""}</span>
                        <span className="text-slate-500">{showIssues ? "Hide" : "Show"}</span>
                    </button>
                    {showIssues && (
                        <div className="max-h-48 overflow-auto border-t border-[#1F2937] divide-y divide-[#1F2937]">
                            {warnings.map((w, i) => (
                                <p key={i} className="px-3 py-1.5 text-[11px] text-slate-400">
                                    <span className="text-slate-600">{[w.sheet, w.row && `row ${w.row}`, w.column].filter(Boolean).join(" · ")}</span>
                                    {(w.sheet || w.row) && " — "}{w.message}
                                </p>
                            ))}
                            {issuesTotal > issues.length && <p className="px-3 py-1.5 text-[11px] text-slate-600">…and {issuesTotal - issues.length} more</p>}
                        </div>
                    )}
                </div>
            )}

            <div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {ITEM_FILTERS.map(f => (
                        <button key={f.id} onClick={() => setFilter(f.id)}
                            className={`px-2.5 py-1 rounded-lg text-[11px] border transition-colors ${filter === f.id ? "bg-blue-500/20 border-blue-500/40 text-blue-300" : "border-[#1F2937] text-slate-400 hover:border-slate-600"}`}>
                            {f.label} <span className="text-slate-500">{items.filter(f.test).length}</span>
                        </button>
                    ))}
                </div>
                <div className="border border-[#1F2937] rounded-lg overflow-auto max-h-72">
                    <table className="w-full text-left text-[11px]">
                        <thead className="sticky top-0 bg-[#0D1520]">
                            <tr>
                                {["Change", "Record", "Party", "Details"].map(h => (
                                    <th key={h} className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#1F2937]">
                            {shown.length === 0 ? (
                                <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                                    {items.length === 0 ? "No differences from the current data." : "Nothing in this category."}
                                </td></tr>
                            ) : shown.slice(0, 500).map(i => (
                                <tr key={i.key} className="align-top">
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                            i.type === "new" ? "bg-green-500/20 text-green-400"
                                            : i.type === "missing" ? "bg-orange-500/20 text-orange-400"
                                            : i.type === "reappeared" ? "bg-cyan-500/20 text-cyan-400"
                                            : "bg-blue-500/20 text-blue-400"}`}>
                                            {i.type === "missing" ? "Missing" : i.type === "reappeared" ? "Back" : i.type === "new" ? "New" : "Changed"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <p className="font-mono text-slate-200">{i.ref}</p>
                                        <p className="text-[10px] text-slate-600">{SHEET_LABELS[i.sheetCode] || i.sheetCode}</p>
                                    </td>
                                    <td className="px-3 py-2 text-slate-400 max-w-[140px] truncate">{i.party || "—"}</td>
                                    <td className="px-3 py-2 text-slate-300 space-y-0.5">
                                        {i.statusChange && (
                                            <p>Status: <span className="text-slate-500">{statusText(i.statusChange.from)}</span>
                                                <ArrowRight size={10} className="inline mx-1"/>{statusText(i.statusChange.to)}</p>
                                        )}
                                        {i.etaChange && (
                                            <p>ETA: <span className="text-slate-500">{fmtDate(i.etaChange.from)}</span>
                                                <ArrowRight size={10} className="inline mx-1"/>{fmtDate(i.etaChange.to)}
                                                {i.etaChange.days != null && i.etaChange.days !== 0 && (
                                                    <span className={i.etaChange.days > 0 ? "text-amber-400 ml-1" : "text-green-400 ml-1"}>
                                                        ({i.etaChange.days > 0 ? "+" : ""}{i.etaChange.days}d)
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                        {i.type === "new" && <p className="text-slate-500">Status: {statusText(i.status)}</p>}
                                        {i.type === "missing" && <p className="text-slate-500">Was {statusText(i.status)} — not in this file</p>}
                                        {i.type !== "new" && i.changes?.filter(c => !duplicatesSummary(c.field, i)).slice(0, 4).map(c => (
                                            <p key={c.field} className="text-slate-500">
                                                {fieldLabel(c.field)}: {fmtValue(c.from)} <ArrowRight size={10} className="inline mx-0.5"/> <span className="text-slate-300">{fmtValue(c.to)}</span>
                                            </p>
                                        ))}
                                        {i.exceptions?.length > 0 && (
                                            <p className="flex flex-wrap gap-1">
                                                {i.exceptions.map(e => (
                                                    <span key={e} className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/15 text-red-400">{EXCEPTION_LABELS[e] || e}</span>
                                                ))}
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {itemsTotal > items.length && (
                    <p className="text-[10px] text-slate-600 mt-1">Showing the first {items.length} of {itemsTotal} differences.</p>
                )}
            </div>

            {isPreview && (
                <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#1F2937] flex-wrap">
                    <p className="text-[11px] text-slate-500 max-w-md">
                        Nothing changes in the Control Tower until you confirm. Confirming re-checks the file against the latest accepted data.
                    </p>
                    <div className="flex gap-2">
                        <button onClick={onDiscard} disabled={busy}
                            className="px-3 py-2 rounded-lg border border-[#374151] text-sm text-slate-300 hover:border-slate-500 disabled:opacity-50">
                            Discard
                        </button>
                        <button onClick={onConfirm} disabled={busy || upload.blocking}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
                            {busy ? <Loader size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                            Confirm import
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main modal ───────────────────────────────────────────────────────────────
export function DevStorageNotice({ storage, className = "" }) {
    if (!storage || storage.persistent) return null;
    return (
        <div className={`bg-amber-900/20 border border-amber-700/50 rounded-lg px-3 py-2 text-[11px] text-amber-300 ${className}`}>
            <span className="font-bold">Development mode:</span> imported reports are held in the backend's memory only.
            They are not saved to Firebase and disappear when the backend restarts.
        </div>
    );
}

export default function ExcelUploadManager({ onClose, onImported, storage }) {
    const [tab, setTab]               = useState("upload");
    const [direction, setDirection]   = useState("inbound");
    const [reportDate, setReportDate] = useState(todayLocalISO());
    const [files, setFiles]           = useState([]);         // queue: previewed one after another
    const [done, setDone]             = useState(0);          // files finished in this batch
    const [busy, setBusy]             = useState(false);
    const [error, setError]           = useState(null);
    const [notice, setNotice]         = useState(null);
    const [preview, setPreview]       = useState(null);
    const [uploads, setUploads]       = useState([]);
    const [histLoading, setHistLoad]  = useState(false);

    const loadHistory = useCallback(async () => {
        setHistLoad(true);
        try {
            const res = await api.get("/api/excel-ingestion/uploads");
            setUploads(res.data.uploads || []);
        } catch (e) {
            setError(apiError(e, "Could not load upload history"));
        } finally {
            setHistLoad(false);
        }
    }, []);

    useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

    // Preview the first file in the queue; the backend recognises which report it is
    const runPreview = async (queue = files) => {
        const file = queue[0];
        if (!file) return;
        setBusy(true); setError(null); setPreview(null);
        try {
            const res = await api.post("/api/excel-ingestion/uploads", file, {
                params: { direction, reportDate, fileName: file.name },
                headers: { "Content-Type": "application/octet-stream" },
            });
            setPreview(res.data);
        } catch (e) {
            // An unrecognised file is skipped so the rest of the batch can continue
            setError(`${file.name}: ${apiError(e, "Upload failed — check the backend is running")}`);
            if (queue.length > 1) next(queue);
            else setFiles([]);
        } finally {
            setBusy(false);
        }
    };
    // Move on to the next file of the batch (after confirm, discard or a failed read)
    const next = (queue = files) => {
        const rest = queue.slice(1);
        setFiles(rest);
        setDone(d => d + 1);
        setPreview(null);
        if (rest.length) runPreview(rest);
    };

    const openUpload = async id => {
        setBusy(true); setError(null); setNotice(null);
        try {
            const res = await api.get(`/api/excel-ingestion/uploads/${id}`);
            setPreview(res.data);
            setTab("upload");
        } catch (e) {
            setError(apiError(e, "Could not open that upload"));
        } finally {
            setBusy(false);
        }
    };

    // Streamed through the API (with auth) so it works in both storage modes
    const download = async id => {
        try {
            const res = await api.get(`/api/excel-ingestion/uploads/${id}/file`, { responseType: "blob" });
            const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "report.xlsx";
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            const status = e.response?.status;
            setError(status === 410 ? "The original file is no longer available (it was held in memory and has been released)." : "Could not get the original file");
        }
    };

    const confirm = async () => {
        const id = preview.upload.id;
        setBusy(true); setError(null);
        try {
            const res = await api.post(`/api/excel-ingestion/uploads/${id}/confirm`);
            const s = res.data.summary || {};
            const label = preview.upload.detected?.label || preview.upload.reportLabel || REPORT_NAMES[preview.upload.reportType];
            setNotice(`${label}: imported ${s.new ?? 0} new, ${s.changed ?? 0} changed, ${s.missing ?? 0} missing.${files.length > 1 ? ` Next file: ${files[1].name}` : " The Control Tower now shows this report."}`);
            onImported?.();
            next();
        } catch (e) {
            setError(apiError(e, "Import failed"));
        } finally {
            setBusy(false);
        }
    };

    const discard = async () => {
        setBusy(true); setError(null);
        try {
            await api.post(`/api/excel-ingestion/uploads/${preview.upload.id}/discard`);
            setNotice("Preview discarded. The file is kept in upload history.");
            next();
        } catch (e) {
            setError(apiError(e, "Could not discard"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.80)", zIndex: 9999 }}>
            <div className="bg-[#111827] border border-[#1F2937] rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">

                <div className="flex items-center justify-between px-6 py-4 border-b border-[#1F2937] shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-white">Excel Upload Manager</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Upload the daily inbound and outbound reports. Preview the changes, then confirm to update the Control Tower.</p>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#1F2937] flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                        <X size={15}/>
                    </button>
                </div>

                <div className="flex gap-1 px-6 pt-3 shrink-0">
                    {[["upload", "New upload", Upload], ["history", "Upload history", History]].map(([id, label, Icon]) => (
                        <button key={id} onClick={() => setTab(id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors ${tab === id ? "bg-[#1F2937] text-white" : "text-slate-400 hover:text-slate-200"}`}>
                            <Icon size={13}/> {label}
                        </button>
                    ))}
                </div>

                <div className="overflow-auto flex-1 p-6 space-y-4">
                    <DevStorageNotice storage={storage}/>
                    {error &&<div className="bg-red-900/30 border border-red-800 rounded-lg px-3 py-2 text-[12px] text-red-400">{error}</div>}
                    {notice && <div className="bg-green-900/20 border border-green-800 rounded-lg px-3 py-2 text-[12px] text-green-400">{notice}</div>}

                    {tab === "upload" && !preview && (
                        <div className="space-y-4">
                            <div className="grid sm:grid-cols-3 gap-3 items-end">
                                <div className="block">
                                    <span className="text-[11px] text-slate-400">Direction</span>
                                    <div className="mt-1 flex bg-[#0D1520] border border-[#1F2937] rounded-lg p-0.5">
                                        {[["inbound", "Inbound"], ["outbound", "Outbound"]].map(([id, label]) => (
                                            <button key={id} onClick={() => setDirection(id)}
                                                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${direction === id ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <label className="block">
                                    <span className="text-[11px] text-slate-400">Report date</span>
                                    <input type="date" value={reportDate} max={todayLocalISO()} onChange={e => setReportDate(e.target.value)}
                                        className="mt-1 w-full bg-[#0D1520] border border-[#1F2937] rounded-lg px-3 py-2 text-sm text-slate-200"/>
                                </label>
                                <label className="block">
                                    <span className="text-[11px] text-slate-400">Report files (.xlsx, .xls, .csv) — several at once is fine</span>
                                    <input type="file" multiple accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                        onChange={e => { setFiles(Array.from(e.target.files || [])); setDone(0); setNotice(null); setError(null); }}
                                        className="mt-1 w-full text-[12px] text-slate-300 file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1F2937] file:text-slate-200"/>
                                </label>
                            </div>
                            {files.length > 0 && (
                                <ul className="text-[12px] text-slate-300 space-y-1">
                                    {files.map(f => <li key={f.name} className="flex items-center gap-2"><FileSpreadsheet size={12} className="text-green-400"/>{f.name}</li>)}
                                </ul>
                            )}
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <p className="text-[11px] text-slate-500 max-w-2xl">
                                    Just pick the direction — each file is recognised from its column headers. {direction === "inbound" ? "Inbound" : "Outbound"}: {DIRECTION_HINT[direction]}.
                                    A file dropped under the wrong direction is moved to the right one, with a warning. The Pallet Tracking workbook is recognised under either.
                                </p>
                                <button onClick={() => runPreview()} disabled={!files.length || !reportDate || busy}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed">
                                    {busy ? <Loader size={14} className="animate-spin"/> : <Eye size={14}/>}
                                    {busy ? "Reading file…" : files.length > 1 ? `Preview ${files.length} files` : "Preview import"}
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "upload" && preview && (
                        <>
                            {files.length + done > 1 && (
                                <p className="text-[11px] text-slate-400">File {done + 1} of {files.length + done}{files.length > 1 && ` · next: ${files[1].name}`}</p>
                            )}
                            {!["preview", "failed"].includes(preview.upload.status) && (
                                <button onClick={() => setPreview(null)} className="text-[11px] text-blue-400 hover:underline">← New upload</button>
                            )}
                            <PreviewPanel data={preview} busy={busy} onConfirm={confirm} onDiscard={discard}
                                onDownload={() => download(preview.upload.id)}/>
                        </>
                    )}

                    {tab === "history" && (
                        <div className="border border-[#1F2937] rounded-lg overflow-x-auto">
                            <table className="w-full text-left text-[11px]">
                                <thead className="bg-[#0D1520]">
                                    <tr>
                                        {["Uploaded", "Report", "Report date", "File", "Rows", "New / Changed / Missing", "Status", ""].map(h => (
                                            <th key={h} className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#1F2937]">
                                    {histLoading ? (
                                        <tr><td colSpan={8} className="px-3 py-8 text-center"><Loader size={18} className="animate-spin mx-auto text-slate-600"/></td></tr>
                                    ) : uploads.length === 0 ? (
                                        <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No uploads yet.</td></tr>
                                    ) : uploads.map(u => (
                                        <tr key={u.id} className="hover:bg-[#1F2937]/40">
                                            <td className="px-3 py-2 text-slate-300 whitespace-nowrap">
                                                {fmtDateTime(u.uploadedAt)}
                                                <p className="text-[10px] text-slate-600">{u.uploadedBy || ""}</p>
                                            </td>
                                            <td className="px-3 py-2 text-slate-300">{u.detected?.label || u.reportLabel || REPORT_NAMES[u.reportType] || u.reportType}</td>
                                            <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtDate(u.reportDate)}</td>
                                            <td className="px-3 py-2 text-slate-400 max-w-[180px] truncate" title={u.fileName}>{u.fileName}</td>
                                            <td className="px-3 py-2 text-slate-300 tabular-nums">{u.summary?.rows ?? "—"}</td>
                                            <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                                                <span className="text-green-400">{u.summary?.new ?? 0}</span>
                                                <span className="text-slate-600"> / </span>
                                                <span className="text-blue-400">{u.summary?.changed ?? 0}</span>
                                                <span className="text-slate-600"> / </span>
                                                <span className="text-orange-400">{u.summary?.missing ?? 0}</span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${UPLOAD_STATUS[u.status]?.c || ""}`}>
                                                    {UPLOAD_STATUS[u.status]?.label || u.status}
                                                </span>
                                                {u.blocking && <span className="ml-1 text-[10px] text-red-400">errors</span>}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right">
                                                <button onClick={() => openUpload(u.id)} className="text-blue-400 hover:underline mr-3">
                                                    {u.status === "preview" ? "Review" : "View"}
                                                </button>
                                                <button onClick={() => download(u.id)} className="text-slate-400 hover:text-white" title="Download original file">
                                                    <Download size={12}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
